import {
  Alert,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { isProduction } from '@shared/utils/common';
import { useMemo, useState } from 'react';
import {
  type AdminMemberCurrentCoach,
  type CreateTestSessionRequest,
  type CreateTestSessionResponse,
  type TestSessionFixtureKind,
  type TestSessionType,
  useCreateTestSessionMutation,
} from '../api/membersApi';
import {
  browserTimeZoneLabel,
  describeTestSessionError,
  formatOffsetIsoMinute,
  isInternalMemberEmail,
  NON_INTERNAL_MEMBER_MESSAGE,
  parseLocalDateTimeMinute,
  sessionHasEnded,
  sessionIsFuture,
  TEST_SESSION_DURATION_MINUTES,
  TEST_SESSION_TYPE_OPTIONS,
} from '../utils/testSessionCreation';

interface CreateTestSessionDialogProps {
  open: boolean;
  memberId: string;
  memberName: string;
  memberEmail: string;
  coaches: AdminMemberCurrentCoach[];
  onClose: () => void;
  onCreated: (result: CreateTestSessionResponse) => void;
}

export const REAL_HISTORY_ACKNOWLEDGEMENT_LABEL =
  "I understand this creates a real Rails session in the member's and coach's history.";

/**
 * MEN-4943: one form, one request. The backend runs the whole recipe and replays
 * it on retry, so a failed attempt keeps every value the admin typed.
 * MEN-5030: the admin also picks the session type; Rails stores it as the
 * ordinary slug, so a Chemistry Call fixture is real chemistry history.
 */
export default function CreateTestSessionDialog({
  open,
  memberId,
  memberName,
  memberEmail,
  coaches,
  onClose,
  onCreated,
}: CreateTestSessionDialogProps) {
  const [coachPersonId, setCoachPersonId] = useState(() =>
    coaches.length === 1 ? coaches[0].person_id : ''
  );
  const [sessionType, setSessionType] = useState<TestSessionType>('coaching_session');
  const [fixtureKind, setFixtureKind] = useState<TestSessionFixtureKind>('completed');
  const [startedAtLocal, setStartedAtLocal] = useState('');
  const [transcript, setTranscript] = useState('');
  const [acknowledged, setAcknowledged] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [createTestSession, { isLoading }] = useCreateTestSessionMutation();

  const timeZone = useMemo(() => browserTimeZoneLabel(), []);
  const startedAt = useMemo(() => parseLocalDateTimeMinute(startedAtLocal), [startedAtLocal]);
  const startedAtError =
    startedAtLocal !== '' && startedAt === null
      ? 'Enter a valid date and time.'
      : startedAt !== null && fixtureKind === 'completed' && !sessionHasEnded(startedAt)
        ? `The session must have already ended (start + ${TEST_SESSION_DURATION_MINUTES} minutes must be in the past).`
        : startedAt !== null && fixtureKind === 'scheduled' && !sessionIsFuture(startedAt)
          ? 'The scheduled session must start in the future.'
          : '';

  // Guidance only: the server is the enforcement boundary. MEN-5455: the
  // internal-domain rule applies in production only.
  const memberDomainAllowed = !isProduction() || isInternalMemberEmail(memberEmail);

  const canSubmit =
    !isLoading &&
    memberDomainAllowed &&
    coachPersonId !== '' &&
    startedAt !== null &&
    startedAtError === '' &&
    (fixtureKind === 'scheduled' || transcript.trim() !== '') &&
    acknowledged;

  const handleClose = () => {
    if (isLoading) return;
    onClose();
  };

  const handleSubmit = async () => {
    if (!canSubmit || startedAt === null) return;
    setSubmitError('');
    try {
      const request: CreateTestSessionRequest = {
        memberId,
        coach_person_id: coachPersonId,
        session_type: sessionType,
        fixture_kind: fixtureKind,
        started_at: formatOffsetIsoMinute(startedAt),
        acknowledge_real_history: true,
      };
      const result = await createTestSession(
        fixtureKind === 'completed' ? { ...request, transcript } : request
      ).unwrap();
      onCreated(result);
    } catch (error) {
      setSubmitError(describeTestSessionError(error));
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle>Create test session for {memberName}</DialogTitle>
      <DialogContent dividers>
        {!memberDomainAllowed && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {NON_INTERNAL_MEMBER_MESSAGE}
          </Alert>
        )}

        <Alert severity="warning" sx={{ mb: 2 }}>
          This creates a real 50-minute session in Rails for the selected member and coach. It
          changes their session counts and history; only its identifier marks it as synthetic. A
          Chemistry Call also counts as real chemistry history for this pair. No calendar event,
          meeting, or Google Doc is created.
        </Alert>

        <FormControl fullWidth sx={{ mb: 2 }} disabled={isLoading}>
          <InputLabel id="test-session-fixture-kind-label">Session state</InputLabel>
          <Select
            labelId="test-session-fixture-kind-label"
            label="Session state"
            value={fixtureKind}
            onChange={(event) => {
              setFixtureKind(event.target.value as TestSessionFixtureKind);
              setSubmitError('');
            }}
            inputProps={{ 'aria-label': 'Session state' }}
          >
            <MenuItem value="completed">Completed, with transcript</MenuItem>
            <MenuItem value="scheduled">Scheduled, without transcript</MenuItem>
          </Select>
          <FormHelperText>
            {fixtureKind === 'scheduled'
              ? 'Creates a future scheduled session with no provider booking, meeting, invitation, or transcript.'
              : 'Creates a completed session and runs transcript readiness and artifact generation.'}
          </FormHelperText>
        </FormControl>

        <FormControl fullWidth sx={{ mb: 2 }} disabled={isLoading}>
          <InputLabel id="test-session-type-label">Session type</InputLabel>
          <Select
            labelId="test-session-type-label"
            label="Session type"
            value={sessionType}
            onChange={(event) => setSessionType(event.target.value as TestSessionType)}
            inputProps={{ 'aria-label': 'Session type' }}
          >
            {TEST_SESSION_TYPE_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
          <FormHelperText>
            {sessionType === 'chem_call'
              ? 'Stored in Rails as a chemistry call. The Chem Call reflection queues through the normal ready workflow after creation.'
              : 'Stored in Rails as a single coaching session.'}
          </FormHelperText>
        </FormControl>

        <FormControl fullWidth sx={{ mb: 2 }} disabled={isLoading}>
          <InputLabel id="test-session-coach-label">Coach</InputLabel>
          <Select
            labelId="test-session-coach-label"
            label="Coach"
            value={coachPersonId}
            onChange={(event) => setCoachPersonId(String(event.target.value))}
            inputProps={{ 'aria-label': 'Coach' }}
          >
            {coaches.map((coach) => (
              <MenuItem key={coach.person_id} value={coach.person_id}>
                {coach.name} ({coach.email})
              </MenuItem>
            ))}
          </Select>
          <FormHelperText>
            {coaches.length > 1
              ? 'This member has more than one current coach; choose one.'
              : 'Current coach of this member.'}
          </FormHelperText>
        </FormControl>

        <TextField
          label={`Session start (${timeZone})`}
          type="datetime-local"
          value={startedAtLocal}
          onChange={(event) => setStartedAtLocal(event.target.value)}
          fullWidth
          disabled={isLoading}
          error={startedAtError !== ''}
          helperText={
            startedAtError ||
            (fixtureKind === 'scheduled'
              ? `Entered in your browser's time zone (${timeZone}). The start must be in the future.`
              : `Entered in your browser's time zone (${timeZone}). The session lasts ${TEST_SESSION_DURATION_MINUTES} minutes and must already be over.`)
          }
          InputLabelProps={{ shrink: true }}
          inputProps={{ step: 60 }}
          sx={{ mb: 2 }}
        />

        {fixtureKind === 'completed' && (
          <TextField
            label="Transcript"
            value={transcript}
            onChange={(event) => setTranscript(event.target.value)}
            multiline
            minRows={10}
            fullWidth
            required
            disabled={isLoading}
            placeholder={'Coach: What would make this week useful?\nMember: I want to practice...'}
            helperText="Paste the transcript text. Readiness needs speaker dialogue or a transcript section; Gemini notes alone are not enough."
            sx={{ mb: 2 }}
          />
        )}

        <FormControlLabel
          control={
            <Checkbox
              checked={acknowledged}
              onChange={(event) => setAcknowledged(event.target.checked)}
              disabled={isLoading}
            />
          }
          label={REAL_HISTORY_ACKNOWLEDGEMENT_LABEL}
        />

        {submitError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {submitError}
          </Alert>
        )}
        {isLoading && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            {fixtureKind === 'scheduled'
              ? 'Creating and projecting the scheduled Rails session…'
              : 'Creating the Rails session, projecting it, attaching the transcript, and queueing artifact generation…'}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!canSubmit}
          startIcon={isLoading ? <CircularProgress size={16} /> : undefined}
        >
          Create Test Session
        </Button>
      </DialogActions>
    </Dialog>
  );
}
